import { cn } from '@my/ui';
import {
  ArrowDownLeft,
  ArrowLeftRight,
  ArrowUpRight,
  Boxes,
  ChevronRight,
  ClipboardCheck,
  MapPin,
  PackageCheck,
  PackagePlus,
  RotateCcw,
  Truck,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import {
  DIRECTION_SECTIONS,
  type GroupMode,
  type Leg,
  type LegStatus,
  type LegType,
  type RosterDriver,
} from '../lib/mockData';

const STATUS_TEXT: Record<LegStatus, string> = {
  Planned: 'text-blue-600',
  Assigned: 'text-slate-500',
  'En Route': 'text-purple-600',
  Delivered: 'text-emerald-600',
  Unassigned: 'text-amber-600',
  'At Risk': 'text-red-600',
};

const TYPE_BADGE: Record<LegType, string> = {
  Collection: 'border-blue-200 bg-blue-50 text-blue-700',
  Delivery: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  'Trunk/Drayage': 'border-slate-300 bg-white text-slate-600',
  Reposition: 'border-slate-300 bg-white text-slate-600',
  'Yard Check': 'border-amber-200 bg-amber-50 text-amber-700',
  'Vehicle Service': 'border-amber-200 bg-amber-50 text-amber-700',
  Shunt: 'border-violet-200 bg-violet-50 text-violet-700',
  'Equipment Run': 'border-pink-200 bg-pink-50 text-pink-700',
};

const TYPE_ICON: Record<LegType, LucideIcon> = {
  Collection: PackagePlus,
  Delivery: PackageCheck,
  'Trunk/Drayage': Truck,
  Reposition: RotateCcw,
  'Yard Check': ClipboardCheck,
  'Vehicle Service': Wrench,
  Shunt: ArrowLeftRight,
  'Equipment Run': Boxes,
};

const FIT_TONE = { ok: 'text-slate-500', warn: 'text-amber-600', risk: 'text-red-600' } as const;

const COLS = 11;

export function LegsTable({
  legs,
  group,
  drivers,
  selectedId,
  onSelect,
}: {
  legs: Leg[];
  group: GroupMode;
  drivers: RosterDriver[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="min-h-0 flex-1 overflow-auto">
      <table className="w-full border-collapse text-xs">
        <thead className="sticky top-0 z-10 bg-slate-700 text-left text-[10px] uppercase tracking-wide text-white">
          <tr>
            <th className="px-3 py-2 font-semibold">Window</th>
            <th className="px-3 py-2 font-semibold">Type</th>
            <th className="px-3 py-2 font-semibold">Ref</th>
            <th className="px-3 py-2 font-semibold">From → To</th>
            <th className="px-3 py-2 font-semibold">Dist/Dur</th>
            <th className="px-3 py-2 font-semibold">Direction</th>
            <th className="px-3 py-2 font-semibold">Asset / Cargo</th>
            <th className="px-3 py-2 font-semibold">Driver</th>
            <th className="px-3 py-2 font-semibold">Status</th>
            <th className="px-3 py-2 font-semibold">Fit</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {group === 'Flat' && legs.map((l) => <Row key={l.id} l={l} selected={l.id === selectedId} onSelect={onSelect} />)}

          {group === 'Driver' && <DriverGroups legs={legs} drivers={drivers} selectedId={selectedId} onSelect={onSelect} />}

          {group === 'Direction' && <DirectionGroups legs={legs} selectedId={selectedId} onSelect={onSelect} />}

          {legs.length === 0 && (
            <tr>
              <td colSpan={COLS} className="px-3 py-8 text-center text-xs italic text-slate-400">
                No legs match this filter.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function DriverGroups({
  legs,
  drivers,
  selectedId,
  onSelect,
}: {
  legs: Leg[];
  drivers: RosterDriver[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const byDriver = new Map<string, Leg[]>();
  const unassigned: Leg[] = [];
  for (const l of legs) {
    if (!l.driver) unassigned.push(l);
    else (byDriver.get(l.driver) ?? byDriver.set(l.driver, []).get(l.driver)!).push(l);
  }
  return (
    <>
      {drivers
        .filter((d) => byDriver.has(d.name))
        .map((d) => (
          <GroupBlock
            key={d.id}
            header={
              <span className="flex items-center justify-between gap-2">
                <span>
                  <span className="font-semibold text-slate-700">{d.name.toUpperCase()}</span>{' '}
                  <span className="text-[10px] normal-case text-slate-400">
                    {d.reg} · Tacho {d.tachoHours}h · {d.onTimePct}%
                  </span>
                </span>
                <button type="button" className="inline-flex items-center gap-0.5 text-[11px] font-medium normal-case text-blue-600 hover:underline">
                  Manage day <ChevronRight className="h-3 w-3" />
                </button>
              </span>
            }
            legs={byDriver.get(d.name)!}
            selectedId={selectedId}
            onSelect={onSelect}
          />
        ))}
      {unassigned.length > 0 && (
        <GroupBlock header={<span className="font-semibold text-slate-700">UNASSIGNED</span>} legs={unassigned} selectedId={selectedId} onSelect={onSelect} />
      )}
    </>
  );
}

function DirectionGroups({
  legs,
  selectedId,
  onSelect,
}: {
  legs: Leg[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <>
      {DIRECTION_SECTIONS.map((s) => {
        const sectionLegs = legs.filter((l) => l.flow === s.flow);
        if (sectionLegs.length === 0) return null;
        return (
          <GroupBlock
            key={s.flow}
            header={<span className="font-semibold uppercase text-slate-700">{s.label}</span>}
            legs={sectionLegs}
            selectedId={selectedId}
            onSelect={onSelect}
          />
        );
      })}
    </>
  );
}

function GroupBlock({
  header,
  legs,
  selectedId,
  onSelect,
}: {
  header: React.ReactNode;
  legs: Leg[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <>
      <tr>
        <td colSpan={COLS} className="border-y border-slate-200 bg-slate-100 px-3 py-1.5 text-[10px] uppercase tracking-wide text-slate-500">
          {header}
        </td>
      </tr>
      {legs.map((l) => (
        <Row key={l.id} l={l} selected={l.id === selectedId} onSelect={onSelect} />
      ))}
    </>
  );
}

function Row({ l, selected, onSelect }: { l: Leg; selected: boolean; onSelect: (id: string) => void }) {
  const TypeIcon = TYPE_ICON[l.type];
  return (
    <tr
      onClick={() => onSelect(l.id)}
      className={cn(
        'cursor-pointer border-t border-slate-200 hover:bg-slate-50',
        selected && 'bg-emerald-50 hover:bg-emerald-50',
      )}
    >
      <td className="whitespace-nowrap px-3 py-1.5 text-slate-500">{l.window ?? '—'}</td>
      <td className="px-3 py-1.5">
        <span className={cn('inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-medium', TYPE_BADGE[l.type])}>
          <TypeIcon className="h-3 w-3" />
          {l.type}
        </span>
      </td>
      <td className="px-3 py-1.5 font-mono text-slate-600">
        {l.ref ? <span className="underline decoration-slate-300">{l.ref}</span> : '—'}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-slate-700">
        {l.from} <span className="text-slate-400">→</span> {l.to}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-slate-500">{l.distDur ?? '—'}</td>
      <td className="px-3 py-1.5">
        <Direction l={l} />
      </td>
      <td className="px-3 py-1.5 text-slate-600">{l.asset ?? '—'}</td>
      <td className="whitespace-nowrap px-3 py-1.5 text-slate-700">
        {l.driver ?? <span className="italic text-slate-400">—</span>}
      </td>
      <td className={cn('whitespace-nowrap px-3 py-1.5 font-medium', STATUS_TEXT[l.status])}>{l.status}</td>
      <td className={cn('whitespace-nowrap px-3 py-1.5', FIT_TONE[l.fitTone])}>{l.fit}</td>
      <td className="px-3 py-1.5 text-right">
        {l.driver === null && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(l.id);
            }}
            className="text-[11px] font-medium text-emerald-700 hover:underline"
          >
            Assign
          </button>
        )}
      </td>
    </tr>
  );
}

function Direction({ l }: { l: Leg }) {
  if (l.flow === 'out')
    return (
      <span className="inline-flex items-center gap-1 text-blue-600">
        <ArrowUpRight className="h-3.5 w-3.5" /> {l.directionLabel}
      </span>
    );
  if (l.flow === 'in')
    return (
      <span className="inline-flex items-center gap-1 text-amber-600">
        <ArrowDownLeft className="h-3.5 w-3.5" /> {l.directionLabel}
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-slate-500">
      <MapPin className="h-3.5 w-3.5" /> {l.directionLabel}
    </span>
  );
}
